import React from 'react';
import { Button } from './ui/button';
import { AlertTriangle, Upload, Cloud, Wifi, CheckCircle2 } from 'lucide-react';

/**
 * Tiny status pill the user can see at a glance — green / yellow / red dot
 * with a short label. Mirrors the backend's `file_status` field.
 */
export function ReplayStatusBadge({ status, sourceType }) {
  const map = {
    available: { color: 'bg-green-500', label: 'Video disponible', icon: CheckCircle2 },
    external:  { color: 'bg-yellow-500', label: 'Video externo',    icon: Cloud },
    missing:   { color: 'bg-red-500',    label: 'Video no encontrado', icon: AlertTriangle },
    unknown:   { color: 'bg-gray-500',   label: 'Estado desconocido', icon: Wifi },
  };
  const m = map[status] || map.unknown;
  const Icon = m.icon;
  return (
    <span
      data-testid={`replay-status-${status}`}
      className="inline-flex items-center gap-1.5 text-[11px] text-gray-200"
      title={sourceType === 'external'
        ? 'El video se sirve desde un enlace externo (Google Drive, Dropbox, etc.)'
        : status === 'available' ? 'El archivo está disponible en el servidor'
        : status === 'missing'   ? 'El archivo no se encuentra — posiblemente se perdió en un redeploy'
        : 'Sin información'}
    >
      <span className={`inline-block w-2 h-2 rounded-full ${m.color}`} />
      <Icon className="w-3 h-3 opacity-70" />
      {m.label}
    </span>
  );
}

/**
 * Full-card friendly state shown INSTEAD of the broken video element when
 * the backend says the file is missing. Includes a 'Re-upload' CTA that the
 * parent wires up to its upload flow.
 */
export function ReplayMissingPlaceholder({ onReupload, replay }) {
  return (
    <div
      className="aspect-video bg-gradient-to-br from-neutral-950 to-neutral-900 border border-red-900/50 rounded flex items-center justify-center"
      data-testid="replay-missing-placeholder"
    >
      <div className="text-center p-6 max-w-md">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-white mb-2">Video no disponible</h3>
        <p className="text-sm text-gray-400 mb-4 leading-relaxed">
          Este video fue cargado localmente y puede haberse perdido durante una
          actualización de la plataforma. La telemetría, anotaciones y configuración
          de overlays se conservan intactas.
        </p>
        <Button
          onClick={onReupload}
          data-testid="replay-reupload-btn"
          className="bg-red-700 hover:bg-red-600"
        >
          <Upload className="w-4 h-4 mr-2" /> Resubir video
        </Button>
        {replay?.video_filename && (
          <p className="text-[10px] text-gray-600 mt-3 font-mono">
            archivo original: {replay.video_filename}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Beta disclaimer banner shown above the upload card. Dismissible per-session
 * via localStorage so we don't nag returning users on every visit.
 */
export function BetaStorageDisclaimer() {
  const [hidden, setHidden] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('datapro_replay_beta_disclaimer_v1') === 'dismissed';
  });
  if (hidden) return null;
  return (
    <div
      data-testid="beta-storage-disclaimer"
      className="bg-amber-950/40 border border-amber-700/50 rounded p-3 text-amber-200 flex items-start gap-3"
    >
      <span className="text-2xl shrink-0">🎥</span>
      <div className="flex-1 text-xs leading-relaxed">
        <strong className="block text-amber-100 mb-0.5">Replay Studio · Beta</strong>
        Los videos cargados localmente pueden perderse después de futuras
        actualizaciones o redeploys de la plataforma. <strong>Conservá siempre
        una copia original</strong> de tus videos. La telemetría, anotaciones y
        configuraciones permanecerán almacenadas.
      </div>
      <button
        type="button"
        onClick={() => {
          localStorage.setItem('datapro_replay_beta_disclaimer_v1', 'dismissed');
          setHidden(true);
        }}
        data-testid="beta-disclaimer-dismiss"
        className="text-[10px] text-amber-300/70 hover:text-amber-200 underline"
      >
        Entendido
      </button>
    </div>
  );
}
